import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

/** Default column widths as percentages (left, center, right). */
const DEFAULT_WIDTHS = [20, 55, 25];

/** Minimum column widths in pixels. */
const MIN_WIDTHS = [200, 320, 220];

/**
 * Clamp percentage widths so each column respects pixel minimums.
 * @param {number[]} widths
 * @param {number} totalPx
 * @returns {number[]}
 */
function clampWidths(widths, totalPx) {
  if (!totalPx || totalPx <= 0) return widths;

  let [left, center, right] = widths;
  let leftPx = (left / 100) * totalPx;
  let centerPx = (center / 100) * totalPx;
  let rightPx = (right / 100) * totalPx;

  const minLeft = MIN_WIDTHS[0];
  const minCenter = MIN_WIDTHS[1];
  const minRight = MIN_WIDTHS[2];

  if (leftPx < minLeft) {
    const diff = minLeft - leftPx;
    leftPx = minLeft;
    centerPx -= diff;
  }
  if (rightPx < minRight) {
    const diff = minRight - rightPx;
    rightPx = minRight;
    centerPx -= diff;
  }
  if (centerPx < minCenter) {
    const diff = minCenter - centerPx;
    centerPx = minCenter;
    const spare = leftPx - minLeft;
    if (spare >= diff) {
      leftPx -= diff;
    } else {
      leftPx = minLeft;
      rightPx -= diff - spare;
    }
  }

  const sum = leftPx + centerPx + rightPx;
  if (sum > 0) {
    leftPx = (leftPx / sum) * totalPx;
    centerPx = (centerPx / sum) * totalPx;
    rightPx = (rightPx / sum) * totalPx;
  }

  return [
    (leftPx / totalPx) * 100,
    (centerPx / totalPx) * 100,
    (rightPx / totalPx) * 100,
  ];
}

function ResizeDivider({ onMouseDown }) {
  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      onMouseDown={onMouseDown}
      sx={{
        width: 6,
        flexShrink: 0,
        cursor: 'col-resize',
        bgcolor: 'divider',
        opacity: 0.6,
        transition: 'background-color 0.15s, opacity 0.15s',
        '&:hover': {
          opacity: 1,
          bgcolor: 'action.selected',
        },
        '&:active': {
          opacity: 1,
          bgcolor: 'primary.main',
        },
      }}
    />
  );
}

/**
 * Three-column resizable layout with drag handles between panes.
 * Stacks vertically on small screens.
 */
export default function ResizableSplitLayout({ left, center, right }) {
  const theme = useTheme();
  const isStacked = useMediaQuery(theme.breakpoints.down('md'));
  const containerRef = useRef(null);
  const [widths, setWidths] = useState(DEFAULT_WIDTHS);
  const dragRef = useRef(null);

  const startDrag = useCallback(
    (dividerIndex) => (event) => {
      event.preventDefault();
      dragRef.current = {
        dividerIndex,
        startX: event.clientX,
        startWidths: [...widths],
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [widths],
  );

  useEffect(() => {
    const onMouseMove = (event) => {
      const drag = dragRef.current;
      const container = containerRef.current;
      if (!drag || !container) return;

      const totalPx = container.getBoundingClientRect().width;
      if (!totalPx) return;

      const deltaPct = ((event.clientX - drag.startX) / totalPx) * 100;
      const [startLeft, startCenter, startRight] = drag.startWidths;

      let next;
      if (drag.dividerIndex === 0) {
        next = clampWidths(
          [startLeft + deltaPct, startCenter - deltaPct, startRight],
          totalPx,
        );
      } else {
        next = clampWidths(
          [startLeft, startCenter + deltaPct, startRight - deltaPct],
          totalPx,
        );
      }
      setWidths(next);
    };

    const onMouseUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  if (isStacked) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 1,
        }}
      >
        <Box sx={{ minHeight: 280, flexShrink: 0 }}>{left}</Box>
        <Box sx={{ minHeight: 360, flexShrink: 0 }}>{center}</Box>
        <Box sx={{ minHeight: 280, flexShrink: 0 }}>{right}</Box>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          flex: `0 0 ${widths[0]}%`,
          minWidth: MIN_WIDTHS[0],
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {left}
      </Box>

      <ResizeDivider onMouseDown={startDrag(0)} />

      <Box
        sx={{
          flex: `0 0 ${widths[1]}%`,
          minWidth: MIN_WIDTHS[1],
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {center}
      </Box>

      <ResizeDivider onMouseDown={startDrag(1)} />

      <Box
        sx={{
          flex: `0 0 ${widths[2]}%`,
          minWidth: MIN_WIDTHS[2],
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {right}
      </Box>
    </Box>
  );
}
